import { Flex, Callout } from "@radix-ui/themes";
import { ApiKeyDetailsCard } from "./api-key-details-card.js";
import {
  getListOrganizationApiKeysQueryKey,
  ListOrganizationApiKeysResponseData,
} from "../../api/endpoint.js";
import { useDeleteOrganizationApiKey } from "../../api/endpoint.js";
import { useQueryClient } from "@tanstack/react-query";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { AlertDialog, Button } from "../elements.js";
import { Translation } from "../i18n/translation.js";

export function RevokeApiKeyDialog({
  apiKey,
  open,
  onOpenChange,
}: {
  apiKey: ListOrganizationApiKeysResponseData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const {
    mutate: deleteOrganizationApiKey,
    isPending,
    error,
    isSuccess,
  } = useDeleteOrganizationApiKey({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListOrganizationApiKeysQueryKey(),
        });
        onOpenChange(false);
      },
    },
  });

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Content maxWidth="529px">
        <AlertDialog.Title mb="2">
          <Translation
            defaultMessage="Revoke API key?"
            id="e18ZsK"
            description="Dialog title for confirming API key revocation"
          />
        </AlertDialog.Title>
        <Flex direction="column" gap="5">
          <AlertDialog.Description>
            <Translation
              defaultMessage="This API key will be immediately disabled. API request made using this key will be rejected, which could cause any systems still depending on it to break."
              id="GLPUiU"
              description="Warning about revoking API key"
            />
            <strong>
              <Translation
                defaultMessage="This action cannot be undone."
                id="PTbRJT"
                description="Warning that API key revocation is permanent"
              />
            </strong>
          </AlertDialog.Description>
          {error && (
            <Callout.Root color="red" role="alert">
              <Callout.Icon>
                <ExclamationTriangleIcon />
              </Callout.Icon>
              <Callout.Text>
                <Translation
                  defaultMessage="An error occurred while revoking the API key."
                  id="edrdLd"
                  description="Error message when API key revocation fails"
                />
              </Callout.Text>
            </Callout.Root>
          )}
          <ApiKeyDetailsCard apiKey={apiKey} />
          <Flex justify="end" gap="2">
            <AlertDialog.Cancel>
              <Button variant="secondary">
                <Translation
                  defaultMessage="Cancel"
                  id="EKf0ES"
                  description="Button to cancel API key revocation"
                />
              </Button>
            </AlertDialog.Cancel>
            <Button
              variant="destructive"
              disabled={isPending || isSuccess}
              loading={isPending || isSuccess}
              onClick={() => deleteOrganizationApiKey({ apiKeyId: apiKey.id })}
            >
              <Translation
                defaultMessage="Revoke key"
                id="KSosDD"
                description="Button to confirm API key revocation"
              />
            </Button>
          </Flex>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
}
